import { useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactiveButton from "reactive-button";
import AuthClient from "../../api/AuthClient";
import DeleteOfferApi from "../../api/DeleteOfferApi";
import BookingType from "../../api/enums/BookingType";
import { OfferSettings, OfferSettingsView } from "../../api/enums/OfferTypes";
import GetOfferSetting from "../Offers/GetOfferSetting";
import { SelectedGroupContext } from "../../GlobalContext/groupDetailContext";
import {
  fetchPromoterSelectedGroup,
  usePromoterSelectedGroup,
} from "../../hooks/usePromoterSelectedGroup";
import { handleClubImgError } from "../../handleImgError";
import { routes } from "../../routes/routes";
import { DateFormat, formatDate } from "../../utils/datePareser";
import { getClubPhoto } from "../../utils/getMedia";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../query/queryKeys";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import Header from "../Header";
import PromoterGroupGenderCounts from "../PromoterGroups/PromoterGroupGenderCounts";
import PromoterGroupPhotoStrip from "../PromoterGroups/PromoterGroupPhotoStrip";
import "../PromotersPages/promotersPages.scss";

const allOfferSettings = [
  OfferSettings.IsThere,
  OfferSettings.IsFreeDrinks,
  OfferSettings.IsFreeEntry,
  OfferSettings.IsFreeDinner,
  OfferSettings.IsFreeTaxi,
  OfferSettings.IsGoodyBag,
  OfferSettings.IsOther,
];

const getBookingTypeLabel = (bookingType: BookingType | string): string => {
  const bookingTypeName =
    typeof bookingType === "string" ? bookingType : BookingType[bookingType];

  switch (bookingTypeName) {
    case "GuestList":
      return "Guestlist";
    case "TableBooking":
      return "Table";
    default:
      return bookingTypeName ?? "";
  }
};

const getOfferSettingText = (option: OfferSettings): string => {
  switch (option) {
    case OfferSettings.IsThere:
      return OfferSettingsView.IsThere;
    case OfferSettings.IsFreeDrinks:
      return OfferSettingsView.FreeDrinks;
    case OfferSettings.IsFreeEntry:
      return OfferSettingsView.FreeEntry;
    case OfferSettings.IsFreeDinner:
      return OfferSettingsView.FreeDinner;
    case OfferSettings.IsFreeTaxi:
      return OfferSettingsView.FreeTaxi;
    case OfferSettings.IsGoodyBag:
      return OfferSettingsView.IsGoodyBag;
    case OfferSettings.IsOther:
      return OfferSettingsView.IsOther;
  }
};

const getMinSpendLabel = (offerType: string): string => {
  switch (offerType) {
    case "GuestList":
      return "Entry Fee (Male):";
    case "TableBooking":
      return "Minimum Spend:";
    default:
      return "";
  }
};

type DeleteOfferRequestState = "idle" | "loading" | "success" | "error";

const PromoterOfferDetails = () => {
  const { city, groupId, offerId } = useParams();
  const { selectedGroup, requestState } = usePromoterSelectedGroup(groupId);
  const { setSelectedGroup } = useContext(SelectedGroupContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteOfferRequestState, setDeleteOfferRequestState] =
    useState<DeleteOfferRequestState>("idle");

  const selectedOffer = useMemo(
    () =>
      selectedGroup?.promotersOffers
        .flatMap((promoterOffer) => promoterOffer.offers)
        .find((offer) => offer.id === offerId),
    [offerId, selectedGroup]
  );
  const invitations = useMemo(
    () => selectedGroup?.invitations ?? [],
    [selectedGroup]
  );
  const selectedOptions = new Set(selectedOffer?.options ?? []);
  const partyCity = selectedGroup?.party.city ?? city ?? "";

  const deleteOffer = async () => {
    if (!offerId || deleteOfferRequestState === "loading") {
      return;
    }

    const api = new DeleteOfferApi(new AuthClient());

    try {
      setDeleteOfferRequestState("loading");
      const response = await api.deleteOffer(offerId);

      if (!response || response.statusCode >= 400) {
        throw new Error("Failed to delete offer.");
      }

      setDeleteOfferRequestState("success");
      if (groupId) {
        const refreshedGroup = await fetchPromoterSelectedGroup(groupId);
        setSelectedGroup(refreshedGroup);
        if (refreshedGroup) {
          queryClient.setQueryData(queryKeys.promoterGroup(groupId), refreshedGroup);
        }
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.promoterGroupsRoot() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.acceptedGroups() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myParties() });
      navigate(routes.PromGroupDetailPage(city ?? "", groupId ?? ""));
    } catch (error) {
      console.error("Failed to delete offer:", error);
      setDeleteOfferRequestState("error");
    }
  };

  return (
    <>
      <Header />
      <div className="centerGroup">
        <div className="desktopUI">
          <div className="row userProfile_Header">
            <Breadcrumbs
              to={routes.PromGroupDetailPage(city ?? "", groupId ?? "")}
            />
            <div className="d-flex justify-content-end space col-6">
              <ReactiveButton
                buttonState={deleteOfferRequestState}
                onClick={() => {
                  void deleteOffer();
                }}
                color="red"
                idleText="Delete"
                loadingText="Deleting"
                successText="Deleted"
                errorText="Error"
                type="button"
                className="btn btn-sm btn-outline-danger"
                outline={true}
                shadow={false}
                rounded={false}
                size="small"
                block={false}
                messageDuration={2000}
                disabled={!offerId || deleteOfferRequestState === "loading"}
                buttonRef={null}
                width={null}
                height={null}
                animation={true}
              />
            </div>
          </div>

          {selectedGroup && selectedOffer ? (
            <div className="promoter-group-detail-page">
              <div className="promoter-group-detail-container">
                <div className="promoter-group-detail-date">
                  {formatDate(
                    new Date(selectedGroup.party.partyDate),
                    false,
                    DateFormat.Short
                  )}
                </div>

                <div className="promoter-group-detail-summary">
                  <span className="promoter-group-detail-summary__label">
                    {getBookingTypeLabel(selectedGroup.party.bookingType)}
                  </span>
                  <span className="promoter-group-detail-summary__city">
                    {selectedGroup.party.city}
                  </span>
                </div>

                <section className="promoter-group-detail-section">
                  <h3 className="promoter-group-detail-section__title">GUESTS</h3>
                  <PromoterGroupPhotoStrip
                    invitations={invitations}
                    city={city}
                    groupId={groupId}
                    callbackUrl="promoter-group-detail"
                    className="promoter-group-detail-photo-strip mt-3"
                    trackClassName="promoter-group-detail-photo-strip__track"
                    imageClassName="promoter-offer-detail-photo-strip__image"
                  />
                </section>

                <div className="promoter-group-detail-counts">
                  <PromoterGroupGenderCounts invitations={invitations} />
                </div>
                <section className="promoter-group-detail__offers">
                  <h3 className="promoter-group-detail__section-title">CLUB</h3>
                  <div className="promoter-group-detail__offer-row">
                    <img
                      src={getClubPhoto({
                        clubName: selectedOffer.clubName,
                        cityName: partyCity,
                      })}
                      alt=""
                      className="promoter-group-detail__club-image"
                      onError={handleClubImgError}
                    />
                    <span className="yellowTxt promoter-group-detail__club-name">
                      {selectedOffer.clubName}
                    </span>
                  </div>
                </section>

                <section className="promoter-group-detail__offers">
                  <h3 className="promoter-group-detail__section-title">{selectedOffer.offerType} DEAL</h3>
                  {Number(selectedOffer.minSpend) > 0 ? (
                    <div className="promoter-offer-detail__min-spend">
                      {getMinSpendLabel(selectedOffer.offerType)}{" "}
                      {selectedOffer.currencySymbol}
                      {selectedOffer.minSpend}
                    </div>
                  ) : null}

                  <div className="promoter-offer-detail__options">
                    {allOfferSettings.map((option) => {
                      const isSelected = selectedOptions.has(option);

                      return (
                        <div
                          className="promoter-offer-detail__option-row"
                          key={option}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="form-check-input checkC shadow-none"
                          />
                          <div className="promoter-offer-detail__option-icon">
                            <GetOfferSetting option={option} isIcon />
                          </div>
                          <span className="yellowTxt promoter-offer-detail__option-label">
                            {getOfferSettingText(option)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>
            </div>
          ) : requestState === "loading" || requestState === "idle" ? (
            <p className="text-white text-center mt-4">Loading offer...</p>
          ) : (
            <p className="text-white text-center mt-4">Offer not found.</p>
          )}
        </div>
      </div>
    </>
  );
};

export default PromoterOfferDetails;
